<template>
  <div class="flex flex-1 flex-col h-full min-h-0">
    <div
      :class="[stickyHeaderTop ? 'sticky z-10' : '', tabContainerClass ?? '']"
      :style="stickyHeaderTop ? { top: `${stickyHeaderTop}px` } : {}"
    >
      <div
        ref="tabScrollRef"
        class="overflow-hidden whitespace-nowrap h-[calc(var(--overview-tab-height)*1px)] relative flex bg-main-background"
        v-hide-keyboard-scroll
        @scroll="onTabScroll"
        @touchstart.passive="onTabTouchStart"
        @touchend.passive="onTabTouchEnd"
      >
        <div class="absolute bg-primary rounded-sm left-0 bottom-0 h-0.5" ref="lineRef"></div>
        <div
          class="inline-block"
          :class="staticTabs ? 'mr-2' : 'mr-[50vw]'"
          :style="{ minWidth: currentTabOffset + 'px' }"
        ></div>
        <div class="flex flex-1 w-full" :class="[staticTabs ? 'justify-around' : '']">
          <div
            v-for="tabConfig in tabConfigsProcessed"
            :key="tabConfig.key"
            v-resize-observer="($event) => setupInitialGeometryMapForTabs(tabConfig, $event)"
            ref="tabs"
            :class="[
              'flex justify-center cursor-pointer',
              $typo.openSans12,
              activeTabKey === tabConfig.key ? 'text-text' : 'text-text-inactive',
            ]"
            :style="{ minWidth: tabWidthMap[tabConfig?.widthKey || ''] + 'px' }"
            data-test-id="dynamic-tab"
            :data-tab-key="tabConfig.key"
            :data-tab-active="activeTabKey === tabConfig.key ? 'true' : 'false'"
            @click="() => onTabClick(tabConfig)"
          >
            {{ tabConfig.title }}
          </div>
        </div>

        <div class="inline-block" :class="staticTabs ? 'mr-2' : 'mr-[50vw]'"></div>
      </div>
    </div>
    <slot name="belowTabs" />
    <div
      ref="contentScrollRef"
      class="overflow-x-scroll overflow-y-hidden flex flex-1 flex-nowrap w-full snap-x snap-mandatory overscroll-x-contain min-h-0"
      v-hide-keyboard-scroll
      @scroll="onContentScroll"
      @touchstart.passive="onContentTouchStart"
      @touchend.passive="onContentTouchEnd"
    >
      <div
        class="min-w-full"
        :style="{ minWidth: currentContentOffset + 'px', height: '100%' }"
      ></div>
      <div
        v-for="tabConfig in tabConfigsProcessed"
        :ref="(el) => setContentElRef(tabConfig.key, el as HTMLElement | null)"
        class="min-w-full snap-start snap-always min-h-0 flex flex-1 overflow-y-hidden h-full"
        :key="tabConfig.key"
        :style="{
          height: props.flexHeight ? '' : pageHeightScroll ? '' : contentHeight,
          transform: `translateY(${tabScrollMap[tabConfig.key]?.topScroll || 0}px)`,
        }"
        data-test-id="dynamic-tab-content"
        :data-tab-key="tabConfig.key"
        v-resize-observer="($event) => setupInitialGeometryMapForContent(tabConfig.key, $event)"
      >
        <template v-if="visibilityMap[tabConfig.key]">
          <slot name="tabContent" :tab-config="tabConfig">
            <component
              v-if="viewPropsTransform && releaseFilter && releaseSorts"
              :ref="(el: unknown) => setPreviewListRef(tabConfig.key, el)"
              :key="tabRefreshKeys[tabConfig.key] ?? 0"
              :style="{ width: windowWidth }"
              :is="PreviewList"
              @openDetails="openDetails"
              @openDetailsNewTab="openDetailsNewTab"
              :noItemsComponent="noItemsComponent"
              :itemMovedCheck="tabConfig.itemMovedCheck"
              v-bind="{ ...viewPropsTransform(tabConfig.tabData, releaseFilter, releaseSorts) }"
            />
          </slot>
        </template>
        <div v-else class="w-full h-full flex items-center justify-center bg-secondary-background">
          <Spinner :size="40" />
        </div>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="pageHeightScroll && gripVisible"
        class="fixed right-0 w-0.75 bg-grey-1 z-10 pointer-events-none transition-opacity duration-300 ease-out"
        :style="gripStyle"
      ></div>
    </transition>
  </div>
</template>

<script lang="ts" setup generic="T, Q extends DocumentNode = DocumentNode">
import {
  type Component,
  computed,
  inject,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  toRefs,
  watch,
} from 'vue'
import { useWindowSize } from '@vueuse/core'
import { vResizeObserver } from '@vueuse/components'
import {
  TabConfig,
  EndlessScrollConfig,
  PreviewListProps,
  ReleaseEntrySorts,
  ReleaseFilterProps,
  PageHeightScrollConfig,
} from '@/utils'
import PreviewList from '@/components/PreviewList.vue'
import type { DocumentNode } from '@/vendor/apollo-client'
import { interpolate } from '@/utils/interpolate.ts'
import $typo from '@/styles/typografy.module.css'
import Spinner from '@/components/Spinner.vue'
import { storeToRefs } from 'pinia'
import { useThemeStore } from '@/stores/theme.ts'
import { useInfoStore } from '@/stores/info.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import { NoveltyRelationTypeType } from '../../__generated__/graphql.ts'
import vHideKeyboardScroll from '@/directives/hide-keyboard-scroll.ts'
import { Capacitor } from '@capacitor/core'

const isNativePlatform = Capacitor.isNativePlatform()

const props = withDefaults(
  defineProps<{
    endlessScroll?: EndlessScrollConfig<T>
    startTabConfigs: TabConfig<T>[]
    releaseFilter?: ReleaseFilterProps
    releaseSorts?: ReleaseEntrySorts
    viewPropsTransform?: (
      tabData: T,
      filter: ReleaseFilterProps,
      sorts: ReleaseEntrySorts,
    ) => PreviewListProps<Q>
    tabWidthMap: { [key: string]: number }
    startKey?: string
    selectTab?: (key: string) => void
    stickyHeaderTop?: number
    staticTabs?: boolean
    linePadding?: number
    tabContainerClass?: string
    pageHeightScroll?: PageHeightScrollConfig
    noItemsComponent?: Component
    flexHeight?: boolean
    skipInitialPreload?: boolean
    belowTabsHeight?: number
  }>(),
  {
    linePadding: 20,
    belowTabsHeight: 0,
  },
)

const {
  viewPropsTransform,
  startKey,
  endlessScroll,
  releaseFilter,
  releaseSorts,
  tabWidthMap,
  staticTabs,
  linePadding,
  pageHeightScroll,
  tabContainerClass,
} = toRefs(props)

const uiStore = useUiStore()
const tabRefreshKeys = ref<Record<string, number>>({})
let horizontalScrollTimeout: number | null = null

type PreviewListExposed = {
  scrollTop: number
  currentSkip: number
  scrollToAndPaginate: (top: number, targetSkip?: number) => Promise<number>
  resetPagination: () => void
}
const previewListRefs = new Map<string, PreviewListExposed>()

function setPreviewListRef(key: string, el: unknown) {
  if (
    el &&
    typeof el === 'object' &&
    'scrollTop' in el &&
    'scrollToAndPaginate' in el &&
    'resetPagination' in el
  ) {
    previewListRefs.set(key, el as PreviewListExposed)
  } else {
    previewListRefs.delete(key)
  }
}

const contentElMap = new Map<string, HTMLElement>()

function setContentElRef(key: string, el: unknown) {
  if (el instanceof HTMLElement) {
    contentElMap.set(key, el)
  } else {
    contentElMap.delete(key)
  }
}

const parentScrollRef = pageHeightScroll.value
  ? inject<Ref<HTMLElement>>('mainScrollContainer')
  : ref(null)
const tabScrollMap = ref<{
  [key: string]: {
    topScroll: number
    savedContentPosition: number
  }
}>({})

type DetailItem = {
  id: string
  entryId?: string
  number?: number
  version?: string
  relationType?: NoveltyRelationTypeType
}

const emit = defineEmits<{
  (e: 'openDetails', item: DetailItem): void
  (e: 'openDetailsNewTab', item: DetailItem): void
}>()

let previousPageScroll: number | null = null

if (pageHeightScroll.value) {
  watch(pageHeightScroll.value, () => {
    const currentPageScroll = pageHeightScroll.value.currentPageScroll
    const scrollDelta = previousPageScroll !== null ? currentPageScroll - previousPageScroll : 0
    previousPageScroll = currentPageScroll

    const tabAreaScroll = Math.max(
      0,
      pageHeightScroll.value.currentPageScroll - pageHeightScroll.value.startTabScrollPosition,
    )
    for (const key in tabScrollMap.value) {
      if (key !== activeTabKey.value) {
        tabScrollMap.value[key].topScroll =
          tabAreaScroll - tabScrollMap.value[key].savedContentPosition
      } else {
        const entry = tabScrollMap.value[key]
        if (entry.topScroll > 0 && parentScrollRef?.value) {
          // Positive gap (tab was shallower than current scroll): collapse instantly
          parentScrollRef.value.scrollTop -= entry.topScroll
          entry.topScroll = 0
        } else if (
          entry.topScroll < 0 &&
          currentPageScroll >= pageHeightScroll.value.startTabScrollPosition
        ) {
          if (scrollDelta < 0 && parentScrollRef?.value) {
            // Scrolling up: absorb the delta into topScroll, prevent page from moving
            entry.topScroll = Math.min(0, entry.topScroll - scrollDelta)
            parentScrollRef.value.scrollTop -= scrollDelta
            previousPageScroll = parentScrollRef.value.scrollTop
          }
        }
      }
    }

    updatePageScrollGrip()
  })
}

onUnmounted(() => {
  if (gripTimer !== null) clearTimeout(gripTimer)
  if (horizontalScrollTimeout !== null) {
    clearTimeout(horizontalScrollTimeout)
    uiStore.horizontalScrolling = false
  }
})

function openDetails(item: DetailItem) {
  emit('openDetails', item)
}

function openDetailsNewTab(item: DetailItem) {
  emit('openDetailsNewTab', item)
}

const { width: windowWidth } = useWindowSize()
const tabScrollRef = ref<HTMLElement | null>(null)
const contentScrollRef = ref<HTMLElement | null>(null)
const lineRef = ref<HTMLElement | null>(null)
let currentTabScrollX = 0
let currentContentScrollX = 0
const currentTabOffset = ref(0)
const currentContentOffset = ref(0)
const tabConfigsProcessed = ref([...props.startTabConfigs]) as Ref<TabConfig<T>[]>

watch(
  () => props.startTabConfigs,
  (newConfigs) => {
    tabConfigsProcessed.value = [...newConfigs]
    // Clear stale geometry so resize observers repopulate it
    for (const key in tabGeometryMap) {
      delete tabGeometryMap[key]
    }
    for (const tabConfig of newConfigs) {
      tabScrollMap.value[tabConfig.key] = { topScroll: 0, savedContentPosition: 0 }
    }
    tabGeometrySorted = []
    visibilityMap.value = {}
    lastActiveTabKey = ''
    startKeyInitialized = false
  },
)

let loadMoreTabs: 'start' | 'end' | null = null
let loadMoreTabsTimeout: number | null = null
let calcVisibilityTimeout: number | null = null

onMounted(() => {
  for (const tabConfig of tabConfigsProcessed.value) {
    tabScrollMap.value[tabConfig.key] = { topScroll: 0, savedContentPosition: 0 }
  }
  currentTabOffset.value = endlessScroll.value?.tabStartOffset || 0
  currentContentOffset.value = endlessScroll.value?.contentStartOffset || 0
})

const tabGeometryMap: {
  [key: string]: {
    key: string
    tabX: number
    tabWidth: number
    contentX: number
    contentWidth: number
  }
} = {}
let tabGeometrySorted: {
  key: string
  tabX: number
  tabWidth: number
  contentX: number
  contentWidth: number
}[] = []
const visibilityMap = ref<{ [key: string]: boolean }>({})
let startKeyInitialized = false
let contentMoveTouched = false
let tabMoveTouched = false
let preloadedTabAmount = 1
const activeTabKey = ref('')
let lastActiveTabKey = ''

onMounted(() => {
  setTimeout(async () => {
    preloadedTabAmount = 6
    if (endlessScroll.value && !props.skipInitialPreload) {
      loadMoreTabs = 'start'
      await loadMoreTabsOnEdges()
      loadMoreTabs = 'end'
      await loadMoreTabsOnEdges()
    }
    setTimeout(() => {
      calcVisibilityMap()
    }, 100)
  }, 100)
})

let isKeepAliveTransition = false

onActivated(() => {
  if (startKeyInitialized) {
    isKeepAliveTransition = true
    contentScrollRef.value?.scrollTo(currentContentScrollX, 0)
    if (!staticTabs.value) {
      tabScrollRef.value?.scrollTo(currentTabScrollX, 0)
    }
    setTimeout(() => {
      isKeepAliveTransition = false
    }, 100)
  }
})

onDeactivated(() => {
  isKeepAliveTransition = true
  setTimeout(() => {
    isKeepAliveTransition = false
  }, 100)
})

function getTabAreaScroll() {
  if (!pageHeightScroll?.value || !parentScrollRef?.value) return 0
  return Math.max(
    0,
    parentScrollRef.value.scrollTop - pageHeightScroll.value.startTabScrollPosition,
  )
}

function saveContentPosition(key: string) {
  const entry = tabScrollMap.value[key]
  if (!entry) return
  entry.savedContentPosition = getTabAreaScroll() - entry.topScroll
}

function restoreContentPosition(key: string) {
  if (!parentScrollRef?.value || !pageHeightScroll?.value) return
  const entry = tabScrollMap.value[key]
  if (!entry) return

  if (parentScrollRef.value.scrollTop <= pageHeightScroll.value.startTabScrollPosition) return

  const tabAreaScroll = getTabAreaScroll()
  if (entry.savedContentPosition <= tabAreaScroll) return

  // Saved position is deeper than current scroll — scroll page down to reach it
  parentScrollRef.value.scrollTop =
    entry.savedContentPosition + pageHeightScroll.value.startTabScrollPosition
  entry.topScroll = 0

  for (const k in tabScrollMap.value) {
    if (k !== key) {
      const other = tabScrollMap.value[k]
      other.topScroll = entry.savedContentPosition - other.savedContentPosition
    }
  }
}

function onTabKeySwitch(oldKey: string, newKey: string) {
  if (!pageHeightScroll?.value || !parentScrollRef?.value) return
  saveContentPosition(oldKey)
  restoreContentPosition(newKey)
}

const calcVisibilityMap = () => {
  for (const key in tabGeometryMap) {
    const tabGeometry = tabGeometryMap[key]
    const contentX = tabGeometry.contentX
    const visibilityWindowLeft =
      (contentScrollRef.value?.scrollLeft || 0) - windowWidth.value * preloadedTabAmount
    const visibilityWindowRight =
      (contentScrollRef.value?.scrollLeft || 0) + windowWidth.value * (preloadedTabAmount + 1)
    visibilityMap.value[key] =
      contentX > visibilityWindowLeft && contentX + windowWidth.value < visibilityWindowRight
  }
}

const computeHorizontalScroll = (calculateVisibility: boolean = true) => {
  const linePaddingHalf = linePadding.value / 2
  const scrollX = currentContentScrollX

  // Current Tab Key
  const distToScrollXVec = tabGeometrySorted.map(({ contentX }) => Math.abs(scrollX - contentX))
  const iOfClosestItem = distToScrollXVec.indexOf(Math.min(...distToScrollXVec))
  const currentTabKey = tabGeometrySorted[iOfClosestItem]?.key || startKey.value || ''

  if (currentTabKey !== lastActiveTabKey) {
    lastActiveTabKey = currentTabKey
    onTabKeySwitch(activeTabKey.value, currentTabKey)
    activeTabKey.value = currentTabKey
    if (startKeyInitialized) {
      props.selectTab?.(currentTabKey)
    }
  }

  // Current scroll direction
  const currentContentMeasure = tabGeometryMap[currentTabKey]
  if (!currentContentMeasure) {
    return
  }
  const currentScrollDirection = currentContentScrollX < currentContentMeasure.contentX ? -1 : 1

  // Next tab key
  const currentTabKeyIndex = tabGeometrySorted.findIndex(({ key }) => key === currentTabKey)
  const nextTabKey =
    tabGeometrySorted[currentTabKeyIndex + currentScrollDirection]?.key || currentTabKey

  // Scroll percentage
  const contentToScrollToGeometry = tabGeometryMap[nextTabKey]
  const normScrollX = scrollX - currentContentMeasure.contentX
  const distance = contentToScrollToGeometry.contentX - currentContentMeasure.contentX
  const scrollPercentage =
    distance === 0 ? 0 : Math.max(Math.min(normScrollX / distance, 1.0), -1.0)

  // Line properties
  const intP = [0, 0.5, 1]
  const currentTabMeasure = tabGeometryMap[currentTabKey]
  const tabToScrollToMeasure = tabGeometryMap[nextTabKey]
  const lineWidth = interpolate(scrollPercentage, intP, [
    currentTabMeasure.tabWidth - linePadding.value,
    currentTabMeasure.tabWidth - linePadding.value + tabToScrollToMeasure.tabWidth,
    tabToScrollToMeasure.tabWidth - linePadding.value,
  ])
  const positionInterpolation =
    currentScrollDirection >= 0
      ? [
          currentTabMeasure.tabX + linePaddingHalf,
          currentTabMeasure.tabX + linePaddingHalf,
          tabToScrollToMeasure.tabX + linePaddingHalf,
        ]
      : [
          currentTabMeasure.tabX + linePaddingHalf,
          tabToScrollToMeasure.tabX + linePaddingHalf,
          tabToScrollToMeasure.tabX + linePaddingHalf,
        ]
  const linePosition = interpolate(scrollPercentage, intP, positionInterpolation)
  currentTabScrollX = interpolate(
    scrollPercentage,
    [0, 1],
    [
      currentTabMeasure.tabX + currentTabMeasure.tabWidth / 2 - windowWidth.value / 2,
      tabToScrollToMeasure.tabX + tabToScrollToMeasure.tabWidth / 2 - windowWidth.value / 2,
    ],
  )
  if (tabScrollRef.value && !staticTabs.value) {
    tabScrollRef.value.scrollLeft = currentTabScrollX
  }
  const lineHeight = interpolate(scrollPercentage, intP, [1, 0.5, 1])
  const lineTop = interpolate(scrollPercentage, intP, [0, 0.5, 0])
  lineRef.value?.style.setProperty(
    'transform',
    `translateX(${linePosition}px) translateY(${lineTop}px) scaleY(${lineHeight})`,
  )
  lineRef.value?.style.setProperty('width', `${lineWidth}px`)

  if (
    loadMoreTabs === null &&
    visibilityMap.value[currentTabKey] === false &&
    calculateVisibility
  ) {
    if (calcVisibilityTimeout) {
      clearTimeout(calcVisibilityTimeout)
    }
    calcVisibilityTimeout = setTimeout(() => {
      calcVisibilityMap()
    }, 100)
  }
}

const tabs = ref<HTMLElement[]>([])

const setupInitialGeometryMapForTabs = (
  config: TabConfig<T>,
  $event: readonly ResizeObserverEntry[],
) => {
  if (isKeepAliveTransition) {
    return
  }
  const key = config.key
  if (!tabConfigsProcessed.value.find((tab) => tab.key === key)) {
    return
  }
  const tabWidth =
    tabWidthMap.value[config?.widthKey || ''] || $event?.[0]?.borderBoxSize?.[0]?.inlineSize || 0
  const tabX = ($event?.[0]?.target as HTMLElement)?.offsetLeft || 0

  tabGeometryMap[key] = tabGeometryMap[key]
    ? { ...tabGeometryMap[key], tabX, tabWidth }
    : { tabX, tabWidth, contentWidth: 0, contentX: 0, key }
  tabGeometrySorted = Object.values(tabGeometryMap)
  tabGeometrySorted.sort((a, b) => a.tabX - b.tabX)
  if (startKey.value === key && tabWidth > 0) {
    currentTabScrollX = tabX + tabWidth / 2 - windowWidth.value / 2
    if (!staticTabs.value) tabScrollRef.value?.scrollTo(currentTabScrollX, 0)
    computeHorizontalScroll()
  }
}

const setupInitialGeometryMapForContent = (key: string, $event: readonly ResizeObserverEntry[]) => {
  if (isKeepAliveTransition) {
    return
  }
  if (!tabConfigsProcessed.value.find((tab) => tab.key === key)) {
    return
  }
  const contentWidth = $event?.[0]?.borderBoxSize?.[0]?.inlineSize || 0
  const contentX = ($event?.[0]?.target as HTMLElement)?.offsetLeft || 0
  tabGeometryMap[key] = tabGeometryMap[key]
    ? { ...tabGeometryMap[key], contentX, contentWidth }
    : { contentX, contentWidth, tabWidth: 0, tabX: 0, key }

  tabGeometrySorted = Object.values(tabGeometryMap)
  tabGeometrySorted.sort((a, b) => a.tabX - b.tabX)

  if (!startKeyInitialized && startKey.value === key && contentWidth > 0) {
    startKeyInitialized = true
    currentContentScrollX = contentX
    contentScrollRef.value?.scrollTo(currentContentScrollX, 0)
    computeHorizontalScroll()
  }
  calcVisibilityMap()
}

watch(startKey, (newKey) => {
  if (!newKey) return
  const geometry = tabGeometryMap[newKey]
  if (!geometry) return
  contentScrollRef.value?.scrollTo({ left: geometry.contentX })
  computeHorizontalScroll()
})

const onContentTouchStart = () => {
  contentMoveTouched = true
}

const onContentTouchEnd = () => {
  contentMoveTouched = false
}

const onTabTouchStart = () => {
  tabMoveTouched = true
}
const onTabTouchEnd = () => {
  tabMoveTouched = false
  onTabScroll()
}

const { currentTheme } = storeToRefs(useThemeStore())
const { height: screenHeight } = useWindowSize()
const { insets } = useInfoStore()

const contentHeight = computed(() => {
  const topOffset =
    (props.stickyHeaderTop
      ? props.stickyHeaderTop + currentTheme.value.layout.overviewTabHeight
      : currentTheme.value.layout.headerHeight +
        currentTheme.value.layout.overviewTabHeight +
        insets.top) + props.belowTabsHeight
  return `calc(${Math.ceil(screenHeight.value - topOffset - currentTheme.value.layout.bottomNavHeight)}px - ${insets.bottom}px)`
})

const numericContentHeight = computed(() => {
  const topOffset =
    (props.stickyHeaderTop
      ? props.stickyHeaderTop + currentTheme.value.layout.overviewTabHeight
      : currentTheme.value.layout.headerHeight +
        currentTheme.value.layout.overviewTabHeight +
        insets.top) + props.belowTabsHeight
  return (
    Math.ceil(screenHeight.value - topOffset - currentTheme.value.layout.bottomNavHeight) -
    insets.bottom
  )
})

const GRIP_MIN_HEIGHT = 24
const GRIP_HIDE_DELAY = 2000
const gripVisible = ref(false)
const gripBarHeight = ref(0)
const gripBarY = ref(0)
let gripTimer: number | null = null

const gripTopOffset = computed(() => {
  return (
    (props.stickyHeaderTop
      ? props.stickyHeaderTop + currentTheme.value.layout.overviewTabHeight
      : currentTheme.value.layout.headerHeight +
        currentTheme.value.layout.overviewTabHeight +
        insets.top) + props.belowTabsHeight
  )
})

const gripStyle = computed(() => ({
  top: `${gripTopOffset.value + gripBarY.value}px`,
  height: `${gripBarHeight.value}px`,
}))

function updatePageScrollGrip() {
  const activeEl = contentElMap.get(activeTabKey.value)
  if (!activeEl) return

  const contentScrollHeight = activeEl.scrollHeight
  const viewportHeight = numericContentHeight.value
  const tabAreaScroll = getTabAreaScroll()

  if (contentScrollHeight <= viewportHeight || tabAreaScroll <= 0) {
    gripVisible.value = false
    return
  }

  const ratio = viewportHeight / contentScrollHeight
  gripBarHeight.value = Math.max(viewportHeight * ratio, GRIP_MIN_HEIGHT)

  const scrollableHeight = contentScrollHeight - viewportHeight
  const scrollPercentage = Math.min(tabAreaScroll / scrollableHeight, 1)
  gripBarY.value = scrollPercentage * (viewportHeight - gripBarHeight.value)

  gripVisible.value = true
  if (gripTimer !== null) clearTimeout(gripTimer)
  gripTimer = window.setTimeout(() => {
    gripVisible.value = false
  }, GRIP_HIDE_DELAY)
}

const onContentScroll = async () => {
  if (isKeepAliveTransition) {
    return
  }
  if (!contentScrollRef.value) return
  currentContentScrollX = contentScrollRef.value.scrollLeft
  computeHorizontalScroll()

  uiStore.horizontalScrolling = true

  if (horizontalScrollTimeout !== null) {
    clearTimeout(horizontalScrollTimeout)
  }
  horizontalScrollTimeout = window.setTimeout(() => {
    uiStore.horizontalScrolling = false
  }, 300)
  if (endlessScroll.value && loadMoreTabs === null) {
    const lastTabGeometry = tabGeometrySorted[tabGeometrySorted.length - 1]
    const firstTabGeometry = tabGeometrySorted[0]
    if (
      lastTabGeometry &&
      currentContentScrollX >= lastTabGeometry.contentX - windowWidth.value * 3
    ) {
      loadMoreTabs = 'end'
    }
    if (
      firstTabGeometry &&
      currentContentScrollX <= firstTabGeometry.contentX + windowWidth.value * 3
    ) {
      loadMoreTabs = 'start'
    }
  }
  if (loadMoreTabs !== null && !contentMoveTouched) {
    if (loadMoreTabsTimeout) {
      clearTimeout(loadMoreTabsTimeout)
    }
    loadMoreTabsTimeout = setTimeout(() => {
      loadMoreTabsOnEdges()
      loadMoreTabs = null
      calcVisibilityMap()
    }, 500)
  }
}

const onTabScroll = async () => {
  if (!tabScrollRef.value || !tabMoveTouched) return
  const scrollLeft = tabScrollRef.value.scrollLeft
  if (endlessScroll.value && loadMoreTabs === null) {
    const lastTabGeometry = tabGeometrySorted[tabGeometrySorted.length - 1]
    const firstTabGeometry = tabGeometrySorted[0]
    if (scrollLeft >= lastTabGeometry.tabX - windowWidth.value * 3) {
      loadMoreTabs = 'end'
    }
    if (scrollLeft <= firstTabGeometry.tabX + windowWidth.value * 2) {
      loadMoreTabs = 'start'
    }
  }
  if (loadMoreTabs !== null) {
    await loadMoreTabsOnEdges()
    loadMoreTabs = null
  }
}

const loadMoreTabsOnEdges = async () => {
  if (!endlessScroll.value || tabGeometrySorted.length === 0) {
    return
  }
  if (loadMoreTabs === 'start') {
    const firstTabGeometry = tabGeometrySorted[0]
    const moreTabs = await endlessScroll.value.loadMoreStart()
    let tabX = firstTabGeometry.tabX
    let contentX = firstTabGeometry.contentX
    moreTabs.reverse()
    if (isNativePlatform) {
      // Native WebViews scroll-anchor automatically: prepended content shifts
      // scrollLeft to keep the visible tab in view. Don't shrink the content
      // spacer (would fight the anchor and cause infinite edge re-trigger).
      // Old tabs' content offsetLeft shifts right by sum(new widths) — update
      // cached contentX since ResizeObserver only tracks size, not position.
      // Use panel width (= contentScrollRef.clientWidth via min-w-full), not
      // windowWidth: insets/scrollbars can make them differ, and any drift
      // overshoots snap-mandatory points → snaps forward by one tab.
      const panelWidth = contentScrollRef.value?.clientWidth ?? windowWidth.value
      const totalContentShift = moreTabs.length * panelWidth
      for (const k in tabGeometryMap) {
        tabGeometryMap[k].contentX += totalContentShift
      }
      for (const t of moreTabs) {
        const tabWidth = tabWidthMap.value[t?.widthKey || ''] || 0
        currentTabOffset.value -= tabWidth
      }
      // New tabs' geometry gets populated by ResizeObserver after layout.
    } else {
      for (const t of moreTabs) {
        const tabWidth = tabWidthMap.value[t?.widthKey || ''] || 0
        const contentWidth = windowWidth.value
        currentTabOffset.value -= tabWidth
        currentContentOffset.value -= windowWidth.value
        tabX -= tabWidth
        contentX -= windowWidth.value
        tabGeometryMap[t.key] = {
          tabX,
          tabWidth,
          contentWidth,
          contentX,
          key: t.key,
        }
      }
    }
    moreTabs.reverse()
    tabGeometrySorted = Object.values(tabGeometryMap)
    tabGeometrySorted.sort((a, b) => a.tabX - b.tabX)
    tabConfigsProcessed.value.unshift(...moreTabs)
    if (isNativePlatform) {
      // Native WebView preserves scrollLeft numerically across DOM mutation.
      // Without spacer shrink, prepended tabs push existing content right by
      // moreTabs.length * panel_width. Re-anchor scrollLeft to the active
      // tab's (now-updated) cached contentX rather than a relative shift —
      // any width mismatch in a relative shift overshoots snap-mandatory and
      // snaps forward one tab.
      await nextTick()
      if (contentScrollRef.value) {
        const anchorKey = activeTabKey.value || startKey.value
        const target = tabGeometryMap[anchorKey]?.contentX
        if (target != null) {
          contentScrollRef.value.scrollLeft = target
          currentContentScrollX = target
        }
      }
    }
  } else {
    const lastTabGeometry = tabGeometrySorted[tabGeometrySorted.length - 1]
    const moreTabs = await endlessScroll.value.loadMoreEnd()
    let tabX = lastTabGeometry.tabX + lastTabGeometry.tabWidth
    let contentX = lastTabGeometry.contentX + lastTabGeometry.contentWidth
    for (const t of moreTabs) {
      const tabWidth = tabWidthMap.value[t?.widthKey || ''] || 0
      const contentWidth = windowWidth.value
      tabGeometryMap[t.key] = {
        tabX,
        tabWidth,
        contentWidth,
        contentX,
        key: t.key,
      }
      tabX += tabWidth
      contentX += windowWidth.value
    }
    tabGeometrySorted = Object.values(tabGeometryMap)
    tabGeometrySorted.sort((a, b) => a.tabX - b.tabX)
    tabConfigsProcessed.value.push(...moreTabs)
  }
}

const onTabClick = (tabConfig: TabConfig<T>) => {
  const geometry = tabGeometryMap[tabConfig.key]
  if (geometry && currentContentScrollX !== geometry.contentX) {
    contentScrollRef.value?.scrollTo({ left: geometry.contentX, behavior: 'smooth' })
  }
}

function getScrollByTab(): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [key, ref] of previewListRefs.entries()) {
    out[key] = ref.scrollTop
  }
  return out
}

function getSkipByTab(): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [key, ref] of previewListRefs.entries()) {
    out[key] = ref.currentSkip
  }
  return out
}

async function scrollTabTo(
  key: string,
  top: number,
  targetSkip?: number,
  maxWaitMs = 5000,
): Promise<number | null> {
  const start = Date.now()
  while (Date.now() - start < maxWaitMs) {
    const list = previewListRefs.get(key)
    if (list) return list.scrollToAndPaginate(top, targetSkip)
    await new Promise((r) => setTimeout(r, 50))
  }
  return null
}

defineExpose({ getScrollByTab, getSkipByTab, scrollTabTo })
</script>
